import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import axios from "axios";
import styles from "./RoasteryPage.module.css";

// --- IMPORT FALLBACK IMAGES (Used if CMS is empty) ---
import banner1 from "../assets/Roastery/banner-1.jpg";
import banner2 from "../assets/Roastery/banner-2.jpg";
import banner3 from "../assets/Roastery/banner-3.jpg";
import sourcingMain from "../assets/Roastery/sourcing-main.jpg";
import roastingMain from "../assets/Roastery/roasting-main.jpg";
import cupMain from "../assets/Roastery/cup-main.jpg";
import wholesale1 from "../assets/Roastery/wholesale-1.jpg";
import wholesale2 from "../assets/Roastery/wholesale-2.jpg";
import wholesale3 from "../assets/Roastery/wholesale-3.jpg";
import export1 from "../assets/Roastery/export-1.jpg";
import export2 from "../assets/Roastery/export-2.jpg";
import behindScenes1 from "../assets/Roastery/behind-scenes-1.jpg";
import recoJebena from "../assets/Roastery/reco-jebena.jpg";
import recoV60 from "../assets/Roastery/reco-v60.jpg";
import recoEspresso from "../assets/Roastery/reco-espresso.jpg";
import recoMokapot from "../assets/Roastery/reco-mokapot.jpg";

function RoasteryPage() {
  const [texts, setTexts] = useState({});
  const [images, setImages] = useState({});

  // 1. Fetch CMS Content
  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get("/api/pages/roastery")
      .then((res) => {
        if (res.data) {
          setTexts(res.data.textFields || {});
          setImages(res.data.imageFields || {});
        }
      })
      .catch((err) => console.log("Using default content"));
  }, []);

  // 2. Helpers to safely retrieve dynamic content or use exact original fallbacks
  const getText = (key, fallback) => texts[key] || fallback;
  const getImage = (key, fallback) => images[key] || fallback;

  // --- DYNAMIC COUNTS FOR REPEATERS ---
  const farmCount = parseInt(getText("farmList_count", "3"));
  const wholesaleCount = parseInt(getText("wholesaleList_count", "5"));
  const recoCount = parseInt(getText("recoList_count", "4"));

  // --- FALLBACK DATA ARRAYS ---
  const farmFallbackImgs = [sourcingMain, roastingMain, cupMain];
  const farmFallbackTitles = ["Sourcing", "Roasting", "Cup"];
  const farmFallbackTexts = [
    "We source our coffee directly from our own farms, partner farmers, and ethical traders, ensuring every bean is rooted in quality and sustainability. From farm to cup, each step of the journey is handled with meticulous care, down to the smallest detail, to bring you coffee that’s as exceptional as the story behind it.",
    "Our coffee beans are roasted by skilled professionals using state-of-the-art equipment and the latest roasting techniques. We offer a wide range of roasts, from light to dark, ensuring something for every palate. What sets us apart is our ability to customize roasts to match our customers' unique flavor profiles, delivering a personalized coffee experience like no other.",
    "Our skilled baristas craft each cup using a variety of methods, from pour-over to traditional Ethiopian jebena, ensuring rich and flavorful coffee every time. We take pride in offering a range of roasts.",
  ];

  const wsFallbackImgs = [
    behindScenes1,
    wholesale3,
    wholesale2,
    export2,
    wholesale1,
  ];
  const wsFallbackTitles = [
    "Quality You Can Trust",
    "Dedicated Support",
    "Custom Roasting",
    "Custom Grinding",
    "Flexible Quantities & Packaging",
  ];
  const wsFallbackTexts = [
    "Our coffee is ethically sourced, carefully processed, and meticulously inspected to maintain the highest standards. With Buncho Coffee, you can confidently serve fresh, flavorful coffee that delights your customers.",
    "Our team is here to help you create the ideal coffee program for your business. Whether it’s designing a custom blend or providing brewing recommendations, we work closely with you to ensure your coffee offerings stand out.",
    "We understand that every business and customer has a distinct flavor preference. That’s why we provide custom roasting services, allowing you to choose the roast profile that best suits your brand or clientele. From light and fruity roasts to bold and rich profiles, we carefully craft each batch to perfection...",
    "We offer custom grinding options to match your preferred brewing methods. Whether it’s coarse grind for French press, medium-fine for Jebena, or espresso-fine for rich shots, we ensure consistent quality with every grind.",
    "We cater to orders of all sizes and offer flexible packaging options, ensuring you get the right amount of coffee delivered in the most convenient format.",
  ];

  const recoFallbackImgs = [recoJebena, recoV60, recoEspresso, recoMokapot];
  const recoFallbackTitles = [
    "Jebena",
    "V60 (Pour-Over Coffee)",
    "Espresso",
    "Moka Pot",
  ];
  const recoFallbackRoasts = [
    "Medium to Medium-Dark. A slightly darker roast enhances the robust and earthy flavors preferred in traditional Ethiopian coffee ceremonies.",
    "Light to Medium. Lighter roasts preserve delicate fruity and floral notes, perfect for showcasing single-origin beans.",
    "Medium-Dark to Dark. This roast level provides the crema, body, and intense flavor typical of espresso.",
    "Medium-Dark to Dark. The moka pot emphasizes bold, rich, and chocolatey flavors best captured in darker roasts.",
  ];
  const recoFallbackGrinds = [
    "Medium-Fine. This allows for a balanced extraction during the slower brewing process in a jebena.",
    "Medium-Fine (similar to table salt). This ensures proper water flow and balanced extraction.",
    "Very Fine (similar to powdered sugar). The fine grind is crucial for proper pressure and extraction in an espresso machine.",
    "Fine (slightly coarser than espresso). Prevents over-extraction while allowing the pressurized water to flow smoothly through the grounds.",
  ];
  const recoFallbackTips = [
    "Allow the grounds to steep thoroughly for full flavor extraction. Serve unfiltered, allowing fine particles to settle naturally. Enhance with butter, sugar, salt, or traditional spices like cardamom or cinnamon, based on preference.",
    "Use a 1:15 coffee-to-water ratio. Pour in slow, circular motions to saturate all the grounds evenly. Aim for a brewing time of 2-4 minutes for optimal flavor clarity.",
    "Use 18–20g of coffee for a double shot, with a brewing time of 25–30 seconds. Adjust grind size and tamping pressure to perfect the flow rate. Serve immediately for the freshest and boldest flavor.",
    "Fill the bottom chamber with hot water to reduce over-heating. Avoid packing the coffee too tightly in the filter basket. Remove from heat as soon as the coffee starts sputtering to avoid bitterness.",
  ];

  return (
    <div className={styles.roasteryPage}>
      <Navbar />

      {/* HERO SECTION */}
      <section className={styles.roasteryHero}>
        <h1>{getText("heroTitle", "Inherited Energy")}</h1>
        <Link to="/products">
          <button className={styles.buyNowBtn}>
            {getText("buyNowBtnText", "Buy Now")}
          </button>
        </Link>
      </section>

      {/* INTRO COLUMNS */}
      <section className={styles.roasteryIntroColumns}>
        <div className={styles.introCol}>
          <img src={getImage("intro1Image", banner1)} alt="Dukamo Coffee cup" />
          <h3>{getText("intro1Title", "Dukamo Coffee")}</h3>
          <p>
            {getText(
              "intro1Text",
              "Everyone deserves good coffee, but some seek great coffee. Dukamo delivers exactly that. Our highend roasted beans are processed at our own station, roasted with intention, and packed with care. Available in 1kg, 500g, and 250g. Find them at our coffee shops or order online.",
            )}
          </p>
        </div>
        <div className={styles.introCol}>
          <img
            src={getImage("intro2Image", banner3)}
            alt="Dukamo Coffee bags"
          />
          <h3>{getText("intro2Title", "Buncho Coffee")}</h3>
          <p>
            {getText(
              "intro2Text",
              "Buncho is a wholesale wing of our company. We roast for hotel, office, bank, café, restaurant, or specialty stores.",
            )}
          </p>
        </div>
      </section>

      {/* FARM TO CUP SECTION (SMART REPEATER) */}
      <section className={styles.roasteryFarmToCup}>
        <h2>{getText("farmMainTitle", "From Farm to Cup")}</h2>
        <div className={styles.farmToCupGrid}>
          {Array.from({ length: farmCount }).map((_, i) => (
            <div className={styles.farmCard} key={i}>
              <img
                src={getImage(
                  `farmList_${i}_image`,
                  farmFallbackImgs[i] || farmFallbackImgs[0],
                )}
                alt={`Farm Step ${i + 1}`}
              />
              <h3>
                {getText(
                  `farmList_${i}_title`,
                  farmFallbackTitles[i] || `Step ${i + 1}`,
                )}
              </h3>
              <p style={{ whiteSpace: "pre-line" }}>
                {getText(
                  `farmList_${i}_text`,
                  farmFallbackTexts[i] || "Description coming soon...",
                )}
              </p>
            </div>
          ))}
        </div>

        {/* --- CENTERED QUOTE --- */}
        <p className={styles.italicQuote}>
          {getText(
            "italicQuote",
            '"In every Ethiopian heart, there is a special place for coffee and the ceremony. However, not all Ethiopians have the knowledge about the fruit they love. A cup of coffee can tell a story of its origin and the flavors it has to offer."',
          )}
        </p>
      </section>

      {/* WHOLESALE SECTION (SMART REPEATER SPLIT FOR CSS GRID) */}
      <section className={styles.roasteryWholesale}>
        <div className={styles.wholesaleHeader}>
          <h2>{getText("wholesaleTitle", "WholeSale")}</h2>
          <p style={{ whiteSpace: "pre-line" }}>
            {getText(
              "wholesaleText",
              "At Buncho Coffee, we are proud to partner with businesses by offering premium wholesale coffee services tailored to meet your unique needs. Whether you're a hotel, office, bank, café, restaurant, or specialty store, our roastery is equipped to deliver exceptional coffee solutions.",
            )}
          </p>
        </div>

        {/* Dynamic Wholesale Grid Split logic to preserve exact CSS classes */}
        <div className={styles.wholesaleGrid2}>
          {Array.from({ length: Math.min(2, wholesaleCount) }).map((_, i) => (
            <div className={styles.wholesaleCard} key={i}>
              <img
                src={getImage(
                  `wholesaleList_${i}_image`,
                  wsFallbackImgs[i] || wsFallbackImgs[0],
                )}
                alt={`Wholesale ${i + 1}`}
              />
              <h3>
                {getText(
                  `wholesaleList_${i}_title`,
                  wsFallbackTitles[i] || `Service ${i + 1}`,
                )}
              </h3>
              <p>
                {getText(
                  `wholesaleList_${i}_text`,
                  wsFallbackTexts[i] || "Details coming soon...",
                )}
              </p>
            </div>
          ))}
        </div>

        {wholesaleCount > 2 && (
          <div className={styles.wholesaleGrid3}>
            {Array.from({ length: wholesaleCount - 2 }).map((_, i) => {
              const actualIndex = i + 2;
              return (
                <div className={styles.wholesaleCard} key={actualIndex}>
                  <img
                    src={getImage(
                      `wholesaleList_${actualIndex}_image`,
                      wsFallbackImgs[actualIndex] || wsFallbackImgs[0],
                    )}
                    alt={`Wholesale ${actualIndex + 1}`}
                  />
                  <h3>
                    {getText(
                      `wholesaleList_${actualIndex}_title`,
                      wsFallbackTitles[actualIndex] ||
                        `Service ${actualIndex + 1}`,
                    )}
                  </h3>
                  <p>
                    {getText(
                      `wholesaleList_${actualIndex}_text`,
                      wsFallbackTexts[actualIndex] || "Details coming soon...",
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.wholesaleContact}>
          <p>{getText("wsContactLabel", "For Wholesale service call")}</p>
          <a href={`tel:${getText("wsPhoneRaw", "+251924222200")}`}>
            {getText("wsPhoneDisplay", "(+251)92-422-22-00")}
          </a>
        </div>
      </section>

      {/* EXPORT SECTION */}
      <section className={styles.roasteryExport}>
        <div className={styles.exportText}>
          <h2>{getText("exportTitle", "Export")}</h2>
          <p style={{ whiteSpace: "pre-line" }}>
            {getText(
              "exportText",
              "We take pride in sharing Ethiopia’s rich coffee heritage with the world. Sourced from the finest coffee-growing regions, our beans are meticulously selected, ethically sourced, and expertly roasted to meet diverse global preferences. We export a variety of Ethiopian coffee, including specialty-grade, single-origin, and custom blends, ensuring quality and consistency in every shipment. We sell roasted whole beans or ground coffee, custom-packed for international markets. We work with businesses, roasters, and retailers to provide coffee under their brand.",
            )}
          </p>

          <div className={styles.exportContact}>
            <p>
              {getText(
                "expContactLabel",
                "To make orders or for more information",
              )}
            </p>
            <p>
              Phone:{" "}
              {getText(
                "expPhone",
                "(+251)91-098-5454 (Direct call or Whatsapp)",
              )}
            </p>
            <p>Wechat ID: {getText("expWechat", "@Rudypooh")}</p>

            <p>
              Email:{" "}
              <a
                href={`mailto:${getText("expEmail1", "Bethlehem@dukamocoffee.com")}`}
              >
                {getText("expEmail1", "Bethlehem@dukamocoffee.com")}
              </a>
            </p>
            <p>
              or{" "}
              <a
                href={`mailto:${getText("expEmail2", "info@bunchocoffee.com")}`}
              >
                {getText("expEmail2", "info@bunchocoffee.com")}
              </a>
            </p>
          </div>
        </div>

        <div className={styles.exportImages}>
          <figure>
            <img
              src={getImage("exportImage", export1)}
              alt="Coffee drying beds"
            />
          </figure>
        </div>
      </section>

      {/* BEHIND THE SCENES SECTION */}
      <section className={styles.roasteryBehindScenes}>
        <div className={styles.behindScenesText}>
          <h2>{getText("behindScenesTitle", "Behind the Scene")}</h2>
          <p>
            {getText(
              "behindScenesText",
              "Behind the scenes of our roasting process, a lot of care and expertise go into creating the perfect coffee. The beans are then carefully roasted to bring out their unique flavors, with each batch being monitored by our skilled roasters. The timing, temperature, and technique are all essential factors, as even the smallest change can impact the final taste. Roasting is a delicate balance between art and science, and we take pride in perfecting each batch.",
            )}
          </p>
        </div>
        <div className={styles.behindScenesImages}>
          <figure>
            <img
              src={getImage("behindScenesImage", banner2)}
              alt="Dukamo Coffee Art"
            />
            <figcaption>
              {getText("behindScenesCaption", "Roasting a 15kg batch")}
            </figcaption>
          </figure>
        </div>
      </section>

      {/* RECOMMENDATIONS SECTION (SMART REPEATER) */}
      <section className={styles.roasteryRecommendations}>
        <h2>
          {getText(
            "recoMainTitle",
            "Roasting, Grinding and Brewing Recommendations",
          )}
        </h2>
        <p>
          {getText(
            "recoIntro",
            "Here's our recommendation for the common brewing practices. These recommendations help highlight the unique characteristics of each brewing method while ensuring the best flavor for every cup. Different people do it differently this is just our suggetion",
          )}
        </p>
        <div className={styles.recoGrid}>
          {Array.from({ length: recoCount }).map((_, i) => (
            <div className={styles.recoCard} key={i}>
              <img
                src={getImage(
                  `recoList_${i}_image`,
                  recoFallbackImgs[i] || recoFallbackImgs[0],
                )}
                alt={`Brewing Method ${i + 1}`}
              />
              <h3>
                {getText(
                  `recoList_${i}_title`,
                  recoFallbackTitles[i] || `Method ${i + 1}`,
                )}
              </h3>
              <p>
                <strong>
                  {getText(`recoList_${i}_roastLabel`, "Roast Level:")}
                </strong>{" "}
                {getText(
                  `recoList_${i}_roast`,
                  recoFallbackRoasts[i] || "Details coming soon...",
                )}
              </p>
              <p>
                <strong>
                  {getText(`recoList_${i}_grindLabel`, "Grind Size:")}
                </strong>{" "}
                {getText(
                  `recoList_${i}_grind`,
                  recoFallbackGrinds[i] || "Details coming soon...",
                )}
              </p>
              <p>
                <strong>
                  {getText(`recoList_${i}_tipsLabel`, "Brewing Tips:")}
                </strong>{" "}
                {getText(
                  `recoList_${i}_tips`,
                  recoFallbackTips[i] || "Details coming soon...",
                )}
              </p>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default RoasteryPage;
